/**
 * useServiceQueue - Hook para gerenciar dados da fila de atendimento
 * 
 * Versão: 4.0.0 - Simplificado para apenas stats, filtros e realtime
 * 
 * Arquitetura:
 * - useQuery: Estatísticas precisas via get_service_queue_stats v1.0.0
 * - Filtros: Gerenciamento centralizado de filtros globais
 * - Realtime: Invalidação de cache ao detectar mudanças
 * 
 * IMPORTANTE: A busca de itens agora é feita por coluna via useServiceQueueColumn
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';
import { useTenantInboxIds, isFromCurrentTenant } from '@/hooks/useTenantInboxIds';

// ============================================================================
// Constantes
// ============================================================================
const STALE_TIME = 30 * 1000; // 30 segundos

// ============================================================================
// Tipos
// ============================================================================

// Tipos de situação na fila
export type QueueSituation = 
  | 'waiting_bot'      // No fluxo do bot (sem departamento e sem agente)
  | 'waiting_agent'    // Aguardando agente (com departamento, sem assigned_to)
  | 'pending_transfer' // Transferência pendente
  | 'in_service'       // Em atendimento (tem assigned_to)
  | 'closed';          // Conversa encerrada

// Item da fila
export interface QueueItem {
  id: string;
  contact: {
    id: string;
    name: string | null;
    phone: string | null;
    avatar_url: string | null;
  } | null;
  inbox: {
    id: string;
    name: string;
    identifier: string;
    // v1.4.0: tipo de canal e slug do provedor para badge de canal no card
    channel_type?: string | null;
    provider_slug?: string | null;
  };
  department: {
    id: string;
    name: string;
  } | null;
  assigned_agent: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;  // v1.2.0: Avatar do agente
  } | null;
  // v1.3.0: dados do grupo (NULL para conversas individuais)
  group?: {
    id: string;
    name: string | null;
    avatar_url: string | null;
  } | null;
  status: 'pending' | 'open' | 'closed';
  situation: QueueSituation;
  created_at: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  waiting_time: number; // Em segundos
  unread_count: number;
  pending_transfer?: {
    id: string;
    to_agent_id: string | null;
    to_agent_name: string | null;
    to_department_id: string | null;
    to_department_name: string | null;
    from_agent_id: string | null;
    from_agent_name: string | null;
    created_at: string;
  };
}

// Estatísticas da fila
export interface QueueStats {
  total: number;
  waiting_bot: number;
  waiting_agent: number;
  pending_transfers: number;
  in_service: number;
  closed: number;  // v4.1.0: Contador de conversas encerradas
  avg_waiting_time: number;
}

// Filtros disponíveis
export interface QueueFilters {
  departmentId: string | null;
  situation: QueueSituation | 'all';
  inboxId: string | null;
  search: string;
  agentId: string | null;
}

// Tipo do retorno da RPC stats
interface RpcQueueStat {
  situation: string;
  count: number;
}

// ============================================================================
// Hook Principal
// ============================================================================

export function useServiceQueue() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const inboxIdsRef = useTenantInboxIds();
  
  // Estado de filtros
  const [filters, setFilters] = useState<QueueFilters>({
    departmentId: null,
    situation: 'all',
    inboxId: null,
    search: '',
    agentId: null,
  });

  // =========================================================================
  // Query de Estatísticas (sem limite de registros)
  // =========================================================================
  const statsQuery = useQuery({
    queryKey: ['service-queue-stats', currentTenant?.id, filters.departmentId, filters.inboxId, filters.agentId, filters.search],
    queryFn: async () => {
      logger.debug('[useServiceQueue]', 'Buscando estatísticas via RPC get_service_queue_stats');
      
      // Chamada RPC com cast para unknown (função não tipada no types.ts)
      const { data, error } = await (supabase.rpc as any)('get_service_queue_stats', {
        p_department_id: filters.departmentId || undefined,
        p_inbox_id: filters.inboxId || undefined,
        p_agent_id: filters.agentId || undefined,
        p_search: filters.search?.trim() || undefined,
      });
      
      if (error) {
        logger.error('[useServiceQueue]', 'Erro na RPC get_service_queue_stats:', error);
        throw error;
      }
      
      const result = (data || []) as unknown as RpcQueueStat[];
      logger.debug('[useServiceQueue]', 'Stats carregados:', result);
      return result;
    },
    enabled: !!currentTenant?.id,
    staleTime: STALE_TIME,
  });

  // Transforma retorno da RPC em QueueStats
  const stats = useMemo((): QueueStats => {
    const data = statsQuery.data || [];
    const getCount = (sit: string) => data.find(s => s.situation === sit)?.count || 0;
    
    return {
      total: data.filter(s => s.situation !== 'avg_wait_seconds').reduce((sum, s) => sum + s.count, 0),
      waiting_bot: getCount('waiting_bot'),
      waiting_agent: getCount('waiting_agent'),
      pending_transfers: getCount('pending_transfer'),
      in_service: getCount('in_service'),
      closed: getCount('closed'),  // v4.1.0: Mapeamento do contador closed
      avg_waiting_time: getCount('avg_wait_seconds'), // v1.1.0: Calculado via RPC usando conversation_action_logs
    };
  }, [statsQuery.data]);

  // =========================================================================
  // Realtime: REMOVIDO em v4.1.0 (2026-06-26)
  // -------------------------------------------------------------------------
  // A subscription antiga que invalidava ['service-queue-column'] e
  // ['service-queue-stats'] a cada evento foi removida porque convivia
  // em paralelo com useServiceQueueRealtime (montado no QueueKanbanBoard),
  // gerando flood de refetch e timeouts (57014) na RPC pesada de stats.
  // O hook unificado faz patch otimista in-cache + invalidação debounced.
  // =========================================================================


  // =========================================================================
  // Ações
  // =========================================================================
  
  /**
   * Atribui conversa a um agente
   */
  const assignConversation = useCallback(async (
    conversationId: string,
    agentId: string
  ): Promise<boolean> => {
    try {
      // RPC atômica server-side: valida tenant, atualiza conversa e
      // aceita transferência pendente em uma única transação.
      const { error } = await supabase.rpc('assign_conversation_to_agent', {
        p_conversation_id: conversationId,
        p_agent_id: agentId,
      });

      if (error) throw error;

      logger.info('[useServiceQueue]', 'Conversa atribuída com sucesso');

      // Invalida cache para refetch
      queryClient.invalidateQueries({ queryKey: ['service-queue-column'] });
      queryClient.invalidateQueries({ queryKey: ['service-queue-stats'] });

      return true;
    } catch (error) {
      logger.error('[useServiceQueue]', `Erro ao atribuir conversa: ${error}`);
      return false;
    }
  }, [queryClient]);

  /**
   * Refetch manual de stats (colunas são invalidadas automaticamente)
   */
  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['service-queue-column'] });
    statsQuery.refetch();
  }, [statsQuery, queryClient]);

  // =========================================================================
  // Retorno
  // =========================================================================
  return {
    // Dados
    stats,
    
    // Estados de loading
    loading: statsQuery.isLoading,
    statsLoading: statsQuery.isLoading,
    
    // Filtros
    filters,
    setFilters,
    
    // Ações
    refetch,
    assignConversation,
  };
}
